import React from "react";
import { useTranslation } from "react-i18next";
import { Table } from "@/shared/widgets/Table";
import { Column } from "@/shared/ui/Blocks";
import { IconName } from "@/shared/ui/Icon";
import { SectionHeader } from "../SectionHeader";
import { cn } from "@/shared/utils/shadcn";

export interface SectionedListColumn<T> {
  className?: string;
  title: string;
  span?: number | "auto";
  accessor?: (data: T) => React.ReactNode | string | undefined;
  sortable?: boolean;
  sortDirection?: "asc" | "desc";
  icon?: IconName;
  hidden?: boolean;
}

export interface SectionData<T> {
  data: T[];
  title?: string;
  description?: string;
  id: string;
  onRowClick?: (item: T) => void;
  columns?: Record<string, SectionedListColumn<T>>;
}

export interface SectionedListProps<T> {
  sections: SectionData<T>[];
  columns: Record<string, SectionedListColumn<T>>;
  loading?: boolean;
  className?: string;
  sectionClassName?: string;
}

function SectionedList<T extends {}>({
  sections,
  columns,
  className,
  sectionClassName,
}: SectionedListProps<T>) {
  const { t } = useTranslation();

  return (
    <Column className={className}>
      {sections.map((section) => (
        <div key={section.id} className={cn("mb-16", sectionClassName)}>
          {(section.title || section.description) && (
            <SectionHeader
              as="h6"
              title={section.title || ""}
              subtitle={section.description}
              className="mb-4"
            />
          )}

          <Table
            columns={section.columns || columns || {}}
            data={section.data}
            onRowClick={section.onRowClick}
            itemsCount={section.data.length}
          />
        </div>
      ))}
    </Column>
  );
}

export { SectionedList };
